<template>
    <div>
        <div class="w-full text-right mb-5">
            <hts-form-button
                v-if="checkPermission(layoutPermissions['costing_gasoline_add'])"
                color="main"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                type="submit"
                @click="openAddGasoline">
                {{ $t('labels.add') }}
            </hts-form-button>
        </div>
        <hts-data-table
            ref="dataTables"
            view-deleted
            :headers="costingGasolineHeaders"
            :server="apiListCostingGasoline"
            :use-archive="useGasOnlineArchive"
            :use-restore="useGasOnlineRestore"
            use-selection
            store-name="costDataTable"
            store-key="Gasoline" />
        <add-gasoline
            ref="addGasoline"
            @refresh-tables="refreshTables()" />
        <edit-gasoline
            ref="editGasoline"
            @refresh-tables="refreshTables()" />
        <hts-modal
            :show="showPopupDeleteCostingGasoline"
            :title="$t('labels.deleteCostingGasoline')"
            type="confirm"
            :content="$t('labels.confirmDeleteCostingGasolineSuccess')"
            @hide="showPopupDeleteCostingGasoline"
            @confirm="handleDeleteCostingGasoline" />
    </div>
</template>

<script setup lang="ts">
import AddGasoline from './-component/gasoline/add.vue'
import EditGasoline from './-component/gasoline/edit.vue'
import { $url } from '#imports'
import layoutPermissions from '@/data/permissions/layout-permissions'
import { checkPermission } from '@/utils/permission-check'
import { ICostingGasoline } from '~/pages/trucker-[id]/cost/-component/imports/form'
import Actions from '~/pages/trucker-[id]/cost/-component/tables/actions.vue'

const { $formatters } = useNuxtApp()

const showPopupDeleteCostingGasoline = ref(false)
const apiListCostingGasoline = $url.cost.gasoline.gasolineList

const addGasoline: any = ref()

const openAddGasoline = () => {
  if (addGasoline.value) {
    addGasoline.value.open()
  }
}
const editGasoline: any = ref()

const openEditGasoline = (data: ICostingGasoline) => {
  if (editGasoline.value) {
    editGasoline.value.open(data._id)
  }
}

const useGasOnlineArchive = computed(() => {
  return { api: $url.cost.gasoline.archiveGasoline, permissions: checkPermission(layoutPermissions.costing_gasoline_archive) }
})
const useGasOnlineRestore = computed(() => {
  return { api: $url.cost.gasoline.restoreGasoline, permissions: checkPermission(layoutPermissions.costing_gasoline_restore) }
})

const costGasolineId = ref('')
const openDeleteAssumption = (data: ICostingGasoline) => {
  costGasolineId.value = data?._id
  showPopupDeleteCostingGasoline.value = true
}
const handleDeleteCostingGasoline = () => {
  const api = $url.cost.gasoline.deleteGasoline
  $api(api, { uriParams: { ID: costGasolineId.value } })
    .then((data: any) => {
      if (!data) {
        return true
      }
      const responseData: any = data.data.value
      if (responseData.success) {
        process.client && $toast().success($t('labels.deleteCostingGasolineSuccess'))
        costGasolineId.value = ''
      }
      refreshTables()
      showPopupDeleteCostingGasoline.value = false
    })
    .catch(() => {
      showPopupDeleteCostingGasoline.value = false
      costGasolineId.value = ''
    })
}

const costingGasolineHeaders = [
  {
    field: 'amount',
    headerName: $t('labels.amountGasolineNotVat'),
    valueGetter: (params: any) => {
      if (typeof params.data.amount !== 'undefined') {
        return $formatters.formatCurrency(params.data.amount)
      }
      return ''
    }
  },
  {
    field: 'availability',
    headerName: $t('labels.effectiveStartTime'),
    valueGetter: (params: any) => {
      if (params.data.availability) {
        return $formatters.datetime(params.data.availability, 'DD-MM-YYYY HH:mm')
      }
      return ''
    }
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    maxWidth: 150,
    valueGetter: (params: any) => {
      if (params.data.createdAt) {
        return $formatters.datetime(params.data.createdAt)
      }
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    maxWidth: 150,
    valueGetter: (params: any) => {
      if (params.data.updatedAt) {
        return $formatters.datetime(params.data.updatedAt)
      }
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    popup: true,
    suppressRowTransform: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: Actions,
    cellRendererParams: {
      storeKey: 'costDataTable',
      storeName: 'Gasoline',
      items: [
        {
          icon: 'hi-pencil-square-icon',
          label: $t('labels.edit'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.costing_gasoline_edit),
          click: openEditGasoline
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-600 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.costing_gasoline_delete),
          click: openDeleteAssumption,
          isArchive: true
        }
      ]
    }
  }
]

const dataTables = ref()

const refreshTables = () => {
  if (dataTables.value) {
    dataTables.value.refresh()
  }
}
</script>
